'use client';
import { useState } from 'react';
import { useIsSM } from '@repo/ui/src/utils/useMediaQuery';
import dynamic from 'next/dynamic';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@repo/ui/src/styles/variables';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { AlignH, AlignV, ImageProps, YoutubeLinkProps } from '@repo/ui/src/types';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';

const VideoRendered = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: false,
});

interface Props {
  toutContent: any;
  youtubeLink?: YoutubeLinkProps;
  cardBackground?: ImageProps;
  horizontalAlignment?: AlignH;
  verticalAlignment?: AlignV;
  fullWidthAndHeight?: boolean;
  noDivider?: boolean;
  className?: string;
}

export const ToutContentCard = ({
  toutContent,
  youtubeLink,
  cardBackground,
  verticalAlignment = VerticalAlignmentEnum.Top,
  horizontalAlignment = HorizontalAlignmentEnum.Left,
  fullWidthAndHeight,
  noDivider,
  className = '',
}: Props) => {
  const smAndUp = useIsSM();
  const xsOnly = !smAndUp;

  const imgUrl = cardBackground
    ? buildImageSrc(
        cardBackground,
        smAndUp ? imageFullWidthSmAndUp : imageFullWidthXs,
        smAndUp ? 5 : 3,
        smAndUp ? 3 : 5,
      )
    : null;

  const { width = 1200 } = useWindowSize();

  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';

  let isVideo;
  let vidWidth;

  if (cardBackground?._meta.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > 1280 ? 1280 : width < 600 ? (width * 140 * 16) / 9 : width;
    if (!vidWidth) {
      // Won't have width on server side, so use ssr media query to get placeholder image width
      vidWidth = xsOnly ? 600 : width * 0.8;
    }
  }

  const centerGradient =
    verticalAlignment === VerticalAlignmentEnum.Center &&
    horizontalAlignment === HorizontalAlignmentEnum.Center;

  const [showVideoDialog, setShowVideoDialog] = useState(false);

  const onContainerClick = () => {
    if (!youtubeLink || showVideoDialog) return;
    setShowVideoDialog(true);
  };

  const alignItems =
    verticalAlignment === VerticalAlignmentEnum.Top
      ? 'items-start'
      : verticalAlignment === VerticalAlignmentEnum.Bottom
        ? 'items-end'
        : 'items-center';

  const justifyContent =
    horizontalAlignment === HorizontalAlignmentEnum.Left
      ? 'justify-start'
      : horizontalAlignment === HorizontalAlignmentEnum.Right
        ? 'justify-end'
        : 'justify-center';

  const gradientDirection = `${
    verticalAlignment === VerticalAlignmentEnum.Center ? '' : verticalAlignment || 'left'
  } ${horizontalAlignment === HorizontalAlignmentEnum.Center ? '' : horizontalAlignment || 'top'}`;

  const getGradientStyle = () => {
    if (!imgUrl) return {};

    if (centerGradient) {
      return {
        background: 'radial-gradient(circle, rgba(0, 0, 0, .5) 18%, rgba(0, 0, 0, 0) 100%)',
      };
    }

    if (smAndUp) {
      return {
        background: `linear-gradient(to ${gradientDirection}, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 90%)`,
      };
    }

    return {
      background: 'linear-gradient(to bottom right, rgba(0, 0, 0, .7) 0%, rgba(0, 0, 0, 0) 100%)',
    };
  };

  return (
    <div
      className={classNames(
        'min-h-[inherit] w-full bg-cover bg-no-repeat bg-center relative',
        youtubeLink && 'cursor-pointer',
        className,
      )}
      onClick={onContainerClick}
      onKeyDown={onContainerClick}
      style={{
        backgroundImage: imgUrl ? `url("${imgUrl}")` : undefined,
      }}
    >
      {isVideo && cardBackground && vidWidth && (
        <VideoRendered
          video={cardBackground}
          width={vidWidth}
          fillContainer
          className="absolute inset-0"
        />
      )}
      <div
        className={classNames(
          'flex min-h-[inherit] relative',
          'p-4',
          smAndUp && 'sm:py-[calc(2*1.6vw)] sm:px-[calc(2*1.6vw)]',
          smAndUp &&
            horizontalAlignment === HorizontalAlignmentEnum.Right &&
            'sm:pl-[35%] md:pl-[55%]',
          smAndUp &&
            horizontalAlignment === HorizontalAlignmentEnum.Left &&
            'sm:pr-[35%] md:pr-[55%]',
          alignItems,
          justifyContent,
          fullWidthAndHeight && 'h-full w-full',
        )}
        style={getGradientStyle()}
      >
        <div
          className={classNames(
            smAndUp && 'sm:[&_.description]:max-w-[440px]',
            toutContent.align === HorizontalAlignmentEnum.Left &&
              smAndUp &&
              'sm:[&_.description]:ml-0 sm:[&_.description]:mr-auto',
            toutContent.align === HorizontalAlignmentEnum.Right &&
              smAndUp &&
              'sm:[&_.description]:mr-0 sm:[&_.description]:ml-auto',
          )}
        >
          <ToutContent
            dark
            divider={!noDivider}
            lineClamp={4}
            mb={noDivider ? 0 : 3}
            align={toutContent.align || 'left'}
            {...toutContent}
            youtubeLink={youtubeLink}
            showVideoDialog={showVideoDialog}
            setShowVideoDialog={setShowVideoDialog}
          />
        </div>
      </div>
    </div>
  );
};
